/**
 * Konfigurimi – lista Server 1..13 me ON/OFF dhe verifikim statusi (OK kur është active).
 * Vetëm SUPERADMIN. Mobile-first.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { BACKEND_URL } from '../config';

const NUM_SERVERS = 13;
const getToken = () => localStorage.getItem('access_token');
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

function ToggleSwitch({ value, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      disabled={disabled}
      onClick={() => !disabled && onChange(!value)}
      className={`w-11 h-6 rounded-full shrink-0 transition-colors relative ${value ? 'bg-green-500' : 'bg-red-500'} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      <span
        className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all duration-200"
        style={{ left: value ? '22px' : '2px' }}
      />
    </button>
  );
}

const Konfigurimi = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState({}); // { [id]: true }

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/servers/status`, { headers: headers() });
      const list = res.data?.servers ?? [];
      setServers(Array.isArray(list) ? list : []);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error(t('noPermission') || 'Nuk keni leje');
        navigate('/me/settings');
        return;
      }
      toast.error(e.response?.data?.detail || 'Gabim në ngarkimin e statusit');
      setServers([]);
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleToggle = async (id, currentlyActive) => {
    setToggling((prev) => ({ ...prev, [id]: true }));
    try {
      if (currentlyActive) {
        await axios.post(`${BACKEND_URL}/api/admin/servers/${id}/stop`, {}, { headers: headers() });
        toast.success(t('serverStopped') || `Server ${id} u ndal`);
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/servers/${id}/start`, {}, { headers: headers() });
        toast.success(t('statusOk') || 'OK – serveri filloi');
      }
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || (currentlyActive ? 'Ndalja dështoi' : 'Nisja dështoi'));
    } finally {
      setToggling((prev) => ({ ...prev, [id]: false }));
    }
  };

  const cardClass = 'flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155] text-left text-gray-800 dark:text-white';
  const iconClass = 'w-5 h-5 shrink-0 text-gray-600 dark:text-gray-400';

  return (
    <Layout>
      <div className="min-h-screen bg-gray-100 dark:bg-[#0f172a] pb-24 md:pb-6">
        <div className="max-w-lg mx-auto px-4 pt-4">
          <div className="flex items-center gap-3 mb-4">
            <button
              type="button"
              onClick={() => navigate('/me/settings')}
              className="p-2 rounded-lg bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#2d3a4f]"
              aria-label={t('back')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{t('konfigurimi') || 'Konfigurimi'}</h1>
          </div>

          {loading ? (
            <div className="py-8 text-center text-gray-500 dark:text-gray-400">{t('loading') || 'Duke u ngarkuar…'}</div>
          ) : (
            <div className="space-y-2">
              {servers.length === 0 && (
                <p className="text-gray-500 dark:text-gray-400 text-sm py-4">{t('noServers') || 'Nuk u gjetën servera.'}</p>
              )}
              {servers.map((s) => {
                const st = (s.status || '').toLowerCase();
                const isOn = st === 'active';
                const busy = toggling[s.id];
                const labelKey = `server${s.id}`;
                const statusText =
                  st === 'active'
                    ? t('statusOk') || 'OK – duke punuar'
                    : st === 'activating' || st === 'deactivating' || st === 'reloading'
                      ? t('statusActivating') || st
                      : st === 'failed'
                        ? t('statusFailed') || 'Failed'
                        : t('statusStopped') || 'Ndalur';
                const statusClass =
                  st === 'active'
                    ? 'text-green-600 dark:text-green-400'
                    : st === 'activating' || st === 'deactivating' || st === 'reloading'
                      ? 'text-amber-600 dark:text-amber-400'
                      : st === 'failed'
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-500 dark:text-gray-400';
                return (
                  <div key={s.id} className={cardClass}>
                    <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <span className="font-medium truncate block">{t(labelKey) || `Server ${s.id}`}</span>
                      <span className={`text-xs ${statusClass}`}>{statusText}</span>
                    </div>
                    <ToggleSwitch
                      value={isOn}
                      onChange={(val) => handleToggle(s.id, isOn)}
                      disabled={busy}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Konfigurimi;
